import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Science as ScienceIcon,
  Kitchen as KitchenIcon,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { authService, recipeService, stockService } from '../services';
import type { Recipe, RecipeComponent } from '../services';
import type { StockItem } from '../services';
import { useToast } from '../hooks/useToast';
import { useDebounce } from '../hooks/useDebounce';

interface RecipeComponentFormData {
  stockItemId: string;
  quantity: string;
  unit: string;
  yieldPercentage?: string;
}

interface RecipeFormData {
  name: string;
  description: string;
  components: RecipeComponentFormData[];
}

const UNIT_OPTIONS = ['UN', 'KG', 'G', 'L', 'ML', 'CX', 'DZ'];

const DEFAULT_FORM: RecipeFormData = {
  name: '',
  description: '',
  components: [],
};

const RecipesManagement: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const userRole = authService.getUserRole();
  const canFilterByCnpj = userRole === 'DEVELOPER';

  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cnpjFilter, setCnpjFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 300);

  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<RecipeFormData>(DEFAULT_FORM);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState<Recipe | null>(null);

  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [stockLoading, setStockLoading] = useState(false);

  const normalizedCnpjFilter = useMemo(() => cnpjFilter.trim(), [cnpjFilter]);

  const loadStockItems = useCallback(async () => {
    try {
      setStockLoading(true);
      const items = await stockService.listStockItems(
        canFilterByCnpj && normalizedCnpjFilter
          ? { cnpj: normalizedCnpjFilter }
          : undefined,
      );
      setStockItems(items);
    } catch (err: any) {
      console.error('[RecipesManagement] Erro ao carregar itens de estoque', err);
      showError(err.message || 'Erro ao carregar itens de estoque');
    } finally {
      setStockLoading(false);
    }
  }, [canFilterByCnpj, normalizedCnpjFilter, showError]);

  const loadRecipes = useCallback(async () => {
    try {
      setLoading(true);
      const params: { cnpj?: string; searchTerm?: string } = {};
      if (canFilterByCnpj && normalizedCnpjFilter) {
        params.cnpj = normalizedCnpjFilter;
      }
      if (debouncedSearch.trim()) {
        params.searchTerm = debouncedSearch.trim();
      }
      const data = await recipeService.listRecipes(Object.keys(params).length ? params : undefined);
      setRecipes(data);
    } catch (err: any) {
      console.error('[RecipesManagement] Erro ao carregar fichas técnicas', err);
      setError(err.message || 'Erro ao carregar fichas técnicas');
    } finally {
      setLoading(false);
    }
  }, [canFilterByCnpj, debouncedSearch, normalizedCnpjFilter]);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  useEffect(() => {
    loadStockItems();
  }, [loadStockItems]);

  const handleOpenCreateDialog = () => {
    setEditingRecipe(null);
    setFormData(DEFAULT_FORM);
    setFormError(null);
    setFormOpen(true);
  };

  const toFormComponent = (component: RecipeComponent): RecipeComponentFormData => ({
    stockItemId: component.stockItemId,
    quantity: component.quantity ? String(component.quantity) : '',
    unit: component.unit,
    yieldPercentage:
      component.yieldPercentage !== undefined ? String(component.yieldPercentage) : undefined,
  });

  const handleOpenEditDialog = (recipe: Recipe) => {
    setEditingRecipe(recipe);
    setFormData({
      name: recipe.name,
      description: recipe.description || '',
      components: (recipe.components || []).map(toFormComponent),
    });
    setFormError(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingRecipe(null);
    setFormError(null);
    setFormData(DEFAULT_FORM);
  };

  const handleAddComponent = () => {
    setFormData((prev) => ({
      ...prev,
      components: [
        ...prev.components,
        {
          stockItemId: '',
          quantity: '',
          unit: 'UN',
          yieldPercentage: '',
        },
      ],
    }));
  };

  const handleRemoveComponent = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      components: prev.components.filter((_, idx) => idx !== index),
    }));
  };

  const handleComponentFieldChange = (
    index: number,
    field: keyof RecipeComponentFormData,
    value: string,
  ) => {
    setFormData((prev) => {
      const next = [...prev.components];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return { ...prev, components: next };
    });
  };

  const handleComponentStockItemChange = (index: number, stockItem: StockItem | null) => {
    setFormData((prev) => {
      const next = [...prev.components];
      next[index] = {
        ...next[index],
        stockItemId: stockItem?.id ?? '',
        unit: stockItem?.unit || next[index].unit || 'UN',
      };
      return { ...prev, components: next };
    });
  };

  const resolveStockItem = useCallback(
    (id: string) => stockItems.find((item) => item.id === id) ?? null,
    [stockItems],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    if (formData.components.length === 0) {
      setFormError('Adicione ao menos um ingrediente na ficha técnica.');
      return;
    }

    const parsedComponents: RecipeComponent[] = [];
    for (const component of formData.components) {
      const stockItemId = component.stockItemId.trim();
      const quantity = Number(component.quantity.replace(',', '.'));
      if (!stockItemId) {
        setFormError('Selecione o ingrediente de cada linha.');
        return;
      }
      if (!component.quantity || Number.isNaN(quantity) || quantity <= 0) {
        setFormError('Informe uma quantidade válida (> 0) para cada ingrediente.');
        return;
      }
      const normalized: RecipeComponent = {
        stockItemId,
        quantity,
        unit: component.unit.trim() || 'UN',
      };
      if (component.yieldPercentage) {
        const yieldValue = Number(component.yieldPercentage.replace(',', '.'));
        if (Number.isNaN(yieldValue) || yieldValue < 0 || yieldValue > 100) {
          setFormError('O rendimento deve estar entre 0 e 100.');
          return;
        }
        normalized.yieldPercentage = yieldValue;
      }
      parsedComponents.push(normalized);
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      components: parsedComponents,
    };

    if (!payload.name) {
      setFormError('Informe um nome para a ficha técnica.');
      return;
    }

    try {
      setFormLoading(true);
      if (editingRecipe) {
        await recipeService.updateRecipe(editingRecipe.id, payload);
        showSuccess('Ficha técnica atualizada com sucesso.');
      } else {
        await recipeService.createRecipe(payload);
        showSuccess('Ficha técnica criada com sucesso.');
      }
      handleCloseForm();
      loadRecipes();
    } catch (err: any) {
      console.error('[RecipesManagement] Erro ao salvar ficha técnica', err);
      setFormError(err.message || 'Erro ao salvar ficha técnica');
    } finally {
      setFormLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!recipeToDelete) return;
    try {
      await recipeService.deleteRecipe(recipeToDelete.id, canFilterByCnpj ? normalizedCnpjFilter || undefined : undefined);
      showSuccess(`Ficha técnica "${recipeToDelete.name}" excluída.`);
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
      loadRecipes();
    } catch (err: any) {
      console.error('[RecipesManagement] Erro ao excluir ficha técnica', err);
      showError(err.message || 'Erro ao excluir ficha técnica');
    }
  };

  const renderComponentRow = (component: RecipeComponentFormData, index: number) => {
    const selectedStock = resolveStockItem(component.stockItemId);

    return (
      <Paper key={`component-${index}`} variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Autocomplete
            fullWidth
            options={stockItems}
            loading={stockLoading}
            value={selectedStock}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => `${option.name}${option.sku ? ` • ${option.sku}` : ''}`}
            onChange={(_, value) => handleComponentStockItemChange(index, value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Ingrediente do estoque"
                required
                helperText={selectedStock ? `Unidade padrão: ${selectedStock.unit}` : undefined}
              />
            )}
            noOptionsText={stockLoading ? 'Carregando...' : 'Nenhum ingrediente encontrado'}
          />

          <TextField
            label="Quantidade"
            value={component.quantity}
            onChange={(event) => handleComponentFieldChange(index, 'quantity', event.target.value)}
            required
            sx={{ width: { xs: '100%', md: 140 } }}
            inputProps={{ inputMode: 'decimal' }}
          />

          <FormControl sx={{ width: { xs: '100%', md: 140 } }}>
            <InputLabel>Unidade</InputLabel>
            <Select
              label="Unidade"
              value={component.unit}
              onChange={(event) => handleComponentFieldChange(index, 'unit', event.target.value)}
            >
              {UNIT_OPTIONS.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
              {!UNIT_OPTIONS.includes(component.unit) && component.unit && (
                <MenuItem value={component.unit}>{component.unit}</MenuItem>
              )}
            </Select>
          </FormControl>

          <TextField
            label="Rendimento %"
            value={component.yieldPercentage || ''}
            onChange={(event) => handleComponentFieldChange(index, 'yieldPercentage', event.target.value)}
            sx={{ width: { xs: '100%', md: 160 } }}
            placeholder="ex: 95"
            inputProps={{ inputMode: 'decimal' }}
            helperText="Opcional"
          />

          <Tooltip title="Remover ingrediente">
            <span>
              <IconButton
                color="error"
                onClick={() => handleRemoveComponent(index)}
                disabled={formLoading}
                sx={{ mt: { xs: 1, md: 0 } }}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Carregando fichas técnicas...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Fichas Técnicas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Estruture ingredientes e rendimentos para reaproveitar receitas de produção nos seus itens.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
          Nova ficha técnica
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            label="Buscar por nome"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Ex.: Massa de pizza"
            fullWidth
          />
          {canFilterByCnpj && (
            <TextField
              label="CNPJ"
              value={cnpjFilter}
              onChange={(event) => setCnpjFilter(event.target.value)}
              placeholder="Somente números"
              sx={{ minWidth: { md: 220 } }}
            />
          )}
          <Button
            variant="outlined"
            onClick={() => {
              loadRecipes();
              loadStockItems();
            }}
          >
            Atualizar
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={1} sx={{ mt: 3 }}>
        {recipes.length === 0 ? (
          <Box p={4} textAlign="center">
            <KitchenIcon sx={{ fontSize: 48, color: 'action.disabled' }} />
            <Typography variant="h6" mt={2}>
              Nenhuma ficha técnica cadastrada
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              Comece criando uma ficha técnica para padronizar o uso de insumos nos seus itens.
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell align="center">Ingredientes</TableCell>
                <TableCell>Atualização</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipes.map((recipe) => (
                <TableRow key={recipe.id} hover>
                  <TableCell>{recipe.name}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    {recipe.description ? recipe.description : (
                      <Typography variant="body2" color="text.secondary">
                        Sem descrição
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={`${recipe.components?.length ?? 0}`} color="primary" size="small" />
                  </TableCell>
                  <TableCell>
                    {new Date(recipe.updatedAt).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenEditDialog(recipe)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setRecipeToDelete(recipe);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={formOpen} onClose={handleCloseForm} fullWidth maxWidth="md">
        <DialogTitle>{editingRecipe ? 'Editar ficha técnica' : 'Nova ficha técnica'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}

            <Stack spacing={2}>
              <TextField
                label="Nome da ficha técnica"
                value={formData.name}
                onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                required
                autoFocus
              />

              <TextField
                label="Descrição"
                value={formData.description}
                onChange={(event) => setFormData((prev) => ({ ...prev, description: event.target.value }))}
                multiline
                minRows={2}
                placeholder="O objetivo desta ficha e observações de produção"
              />

              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="subtitle1">Ingredientes</Typography>
                  <Button variant="outlined" onClick={handleAddComponent} startIcon={<AddIcon />}>
                    Adicionar ingrediente
                  </Button>
                </Box>

                <Stack spacing={2}>
                  {formData.components.length === 0 && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum ingrediente adicionado ainda. Use o botão acima para incluir.
                      </Typography>
                    </Paper>
                  )}

                  {formData.components.map((component, index) => renderComponentRow(component, index))}
                </Stack>
              </Box>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseForm} disabled={formLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={formLoading} startIcon={formLoading ? <CircularProgress size={18} /> : <ScienceIcon />}>
              {editingRecipe ? 'Salvar alterações' : 'Criar ficha técnica'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Excluir ficha técnica</DialogTitle>
        <DialogContent>
          <Typography>
            Confirma a exclusão da ficha técnica
            {recipeToDelete ? ` "${recipeToDelete.name}"` : ''}? Esta ação não poderá ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button color="error" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RecipesManagement;
